import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { ViewNav } from "@/components/ViewNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { XeroPanel } from "@/components/XeroPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Trash2, Plus, Loader2, Pencil, QrCode, Printer } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const Card = ({ title, desc, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
    <div>
      <h3 className="font-display font-bold text-slate-900">{title}</h3>
      {desc && <p className="text-sm text-slate-500">{desc}</p>}
    </div>
    {children}
  </div>
);

const SITE_ACCESS = [
  { value: "all", label: "All Sites" },
  { value: "Otorohanga", label: "Otorohanga" },
  { value: "Razza", label: "Razza" },
  { value: "Te Kuiti", label: "Te Kuiti" },
];

const UsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "staff", site_access: "all" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ role: "staff", site_access: "all", password: "" });
  const [editBusy, setEditBusy] = useState(false);
  const load = () => api.adminListUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ role: u.role || "staff", site_access: u.site_access || "all", password: "" });
  };
  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const body = { role: editForm.role, site_access: editForm.site_access };
      if (editForm.password.trim()) body.password = editForm.password.trim();
      await api.adminUpdateUser(editing.id, body);
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEditBusy(false); }
  };

  const add = async () => {
    if (!form.username.trim() || !form.password) { toast.error("Username and password required"); return; }
    setBusy(true);
    try { await api.adminCreateUser(form); toast.success("User added"); setForm({ username: "", password: "", name: "", role: "staff", site_access: "all" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.username}?`)) return;
    try { await api.adminDeleteUser(u.id); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const siteLabel = (v) => SITE_ACCESS.find((s) => s.value === v)?.label || "All Sites";

  return (
    <Card title="App Users" desc="Add or remove logins. Site access limits a user to a single facility's data and tabs.">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <div className="space-y-1"><Label>Username</Label><Input data-testid="admin-user-username" value={form.username} onChange={(e) => set("username", e.target.value)} /></div>
        <div className="space-y-1"><Label>Password</Label><Input data-testid="admin-user-password" type="text" value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
        <div className="space-y-1"><Label>Name</Label><Input data-testid="admin-user-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div className="space-y-1"><Label>Role</Label>
          <Select value={form.role} onValueChange={(v) => set("role", v)}>
            <SelectTrigger data-testid="admin-user-role"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label>Site Access</Label>
          <Select value={form.site_access} onValueChange={(v) => set("site_access", v)}>
            <SelectTrigger data-testid="admin-user-site-access"><SelectValue /></SelectTrigger>
            <SelectContent>{SITE_ACCESS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="admin-add-user" onClick={add} disabled={busy} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-xs uppercase text-slate-500 text-left border-b border-slate-200"><th className="py-2">Username</th><th>Name</th><th>Role</th><th>Site Access</th><th></th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-slate-100" data-testid={`admin-user-row-${u.username}`}>
              <td className="py-2 font-medium text-slate-800">{u.username}</td>
              <td className="text-slate-600">{u.name}</td>
              <td><span className={`text-xs rounded-full px-2 py-0.5 ${u.role === "admin" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{u.role}</span></td>
              <td><span className="text-xs rounded-full px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200">{siteLabel(u.site_access)}</span></td>
              <td className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => openEdit(u)} className="text-slate-400 hover:text-blue-600 p-1" data-testid={`admin-edit-user-${u.username}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(u)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`admin-delete-user-${u.username}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="admin-edit-dialog">
          <DialogHeader><DialogTitle>Edit {editing?.username}</DialogTitle><DialogDescription>Update this user's role, site access, or set a new password.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1"><Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="admin-edit-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Site Access</Label>
              <Select value={editForm.site_access} onValueChange={(v) => setEditForm((f) => ({ ...f, site_access: v }))}>
                <SelectTrigger data-testid="admin-edit-site-access"><SelectValue /></SelectTrigger>
                <SelectContent>{SITE_ACCESS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>New Password <span className="text-slate-400 font-normal">(leave blank to keep current)</span></Label>
              <Input data-testid="admin-edit-password" type="text" value={editForm.password} onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="admin-edit-save" onClick={saveEdit} disabled={editBusy} className="bg-blue-600 hover:bg-blue-700">{editBusy ? "Saving..." : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const SitesPanel = () => {
  const [sites, setSites] = useState([]);
  const [siteName, setSiteName] = useState("");
  const [shed, setShed] = useState({ site: "", unit_number: "", size: "", status: "vacant" });
  const load = () => api.getSites().then(setSites).catch(() => {});
  useEffect(() => { load(); }, []);

  const addSite = async () => {
    if (!siteName.trim()) { toast.error("Site name required"); return; }
    try { await api.adminAddSite({ name: siteName }); toast.success("Site added"); setSiteName(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delSite = async (s) => {
    if (!window.confirm(`Delete site ${s.name}?`)) return;
    try { await api.adminDeleteSite(s.key); toast.success("Site deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addShed = async () => {
    if (!shed.site || !shed.unit_number.trim()) { toast.error("Site and unit number required"); return; }
    try { await api.adminCreateUnit(shed); toast.success(`Shed ${shed.unit_number} added`); setShed({ site: "", unit_number: "", size: "", status: "vacant" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const ss = (k, v) => setShed((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <Card title="Sites / Locations" desc="Add new facility locations. Sites with units can't be deleted until emptied.">
        <div className="flex gap-2 max-w-md">
          <Input data-testid="admin-site-name" value={siteName} onChange={(e) => setSiteName(e.target.value)} placeholder="New site name (e.g. Te Awamutu)" />
          <Button data-testid="admin-add-site" onClick={addSite} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add Site</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {sites.map((s) => (
            <span key={s.key} data-testid={`admin-site-${s.key}`} className="inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-full px-3 py-1 text-sm">
              <span className="font-medium text-slate-700">{s.name}</span>
              <span className="text-xs font-mono-nums text-slate-400">{s.prefix}</span>
              <button onClick={() => delSite(s)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
      </Card>

      <Card title="Add Shed / Unit" desc="Create an individual storage unit. Delete units from the unit detail panel on the Units page.">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
          <div className="space-y-1"><Label>Site</Label>
            <Select value={shed.site} onValueChange={(v) => ss("site", v)}>
              <SelectTrigger data-testid="admin-shed-site"><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>Unit Number</Label><Input data-testid="admin-shed-number" value={shed.unit_number} onChange={(e) => ss("unit_number", e.target.value)} placeholder="e.g. OTO-200" /></div>
          <div className="space-y-1"><Label>Size</Label><Input data-testid="admin-shed-size" value={shed.size} onChange={(e) => ss("size", e.target.value)} placeholder="e.g. 6x3" /></div>
          <div className="space-y-1"><Label>Status</Label>
            <Select value={shed.status} onValueChange={(v) => ss("status", v)}>
              <SelectTrigger data-testid="admin-shed-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="vacant">Vacant</SelectItem><SelectItem value="occupied">Occupied</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem><SelectItem value="needs_cleaning">Needs Cleaning</SelectItem></SelectContent>
            </Select>
          </div>
          <Button data-testid="admin-add-shed" onClick={addShed} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add Shed</Button>
        </div>
      </Card>
    </div>
  );
};

const PricingPanel = () => {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.getPricing().then(setP); }, []);
  if (!p) return <div className="text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>;
  const set = (k, v) => setP((f) => ({ ...f, [k]: v }));
  const save = async () => {
    const nums = [p.power_rate, p.water_rate, ...(p.size_rates || []).filter((r) => r.size.trim()).flatMap((r) => [r.weekly, r.monthly]), ...(p.fees || []).filter((f) => f.name.trim()).map((f) => f.amount)];
    if (nums.some((v) => v === "" || isNaN(Number(v)) || Number(v) < 0)) {
      toast.error("Fill in every price with a valid amount before saving");
      return;
    }
    setBusy(true);
    try {
      const updated = await api.adminUpdatePricing({
        power_rate: Number(p.power_rate), water_rate: Number(p.water_rate),
        size_rates: (p.size_rates || []).filter((r) => r.size.trim()).map((r) => ({ size: r.size.trim(), weekly: Number(r.weekly), monthly: Number(r.monthly) })),
        fees: (p.fees || []).filter((f) => f.name.trim()).map((f) => ({ name: f.name.trim(), amount: Number(f.amount) })),
      });
      setP(updated); toast.success("Pricing updated — now live in utility logs");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const Field = ({ label, k, unit }) => (
    <div className="space-y-1" key={k}>
      <Label>{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
        <Input data-testid={`pricing-${k}`} type="number" step="any" value={p[k]} onChange={(e) => set(k, e.target.value)} className="pl-6" />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{unit}</span>
      </div>
    </div>
  );
  const setRow = (list, i, k, v) => setP((f) => ({ ...f, [list]: f[list].map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const addRow = (list, row) => setP((f) => ({ ...f, [list]: [...(f[list] || []), row] }));
  const delRow = (list, i) => setP((f) => ({ ...f, [list]: f[list].filter((_, j) => j !== i) }));
  const cell = "h-8 text-sm";
  return (
    <div className="space-y-4">
    <Card title="Shed Pricing" desc="Weekly and monthly rent per shed size. All pricing includes GST.">
      <div className="overflow-x-auto max-w-2xl">
        <table className="w-full text-sm" data-testid="pricing-size-table">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="py-2 pr-2">Size (m)</th><th className="py-2 pr-2">Weekly $</th><th className="py-2 pr-2">Monthly $</th><th /></tr></thead>
          <tbody>
            {(p.size_rates || []).map((r, i) => (
              <tr key={`size-${i}`} data-testid={`pricing-size-row-${i}`}>
                <td className="py-1 pr-2"><Input data-testid={`pricing-size-${i}`} value={r.size} onChange={(e) => setRow("size_rates", i, "size", e.target.value)} className={cell} /></td>
                <td className="py-1 pr-2"><Input data-testid={`pricing-weekly-${i}`} type="number" step="any" value={r.weekly} onChange={(e) => setRow("size_rates", i, "weekly", e.target.value)} className={cell} /></td>
                <td className="py-1 pr-2"><Input data-testid={`pricing-monthly-${i}`} type="number" step="any" value={r.monthly} onChange={(e) => setRow("size_rates", i, "monthly", e.target.value)} className={cell} /></td>
                <td className="py-1"><button data-testid={`pricing-size-delete-${i}`} onClick={() => delRow("size_rates", i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button variant="outline" size="sm" data-testid="pricing-add-size" onClick={() => addRow("size_rates", { size: "", weekly: "", monthly: "" })}><Plus className="h-4 w-4 mr-1" /> Add size</Button>
    </Card>
    <Card title="Other Fees" desc="One-off charges.">
      <div className="space-y-2 max-w-md">
        {(p.fees || []).map((f, i) => (
          <div key={`fee-${i}`} className="flex items-center gap-2" data-testid={`pricing-fee-row-${i}`}>
            <Input data-testid={`pricing-fee-name-${i}`} value={f.name} onChange={(e) => setRow("fees", i, "name", e.target.value)} className={cell} />
            <Input data-testid={`pricing-fee-amount-${i}`} type="number" step="any" value={f.amount} onChange={(e) => setRow("fees", i, "amount", e.target.value)} className={`${cell} w-28`} />
            <button data-testid={`pricing-fee-delete-${i}`} onClick={() => delRow("fees", i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" data-testid="pricing-add-fee" onClick={() => addRow("fees", { name: "", amount: "" })}><Plus className="h-4 w-4 mr-1" /> Add fee</Button>
    </Card>
    <Card title="Utility Rates" desc="Used for the J1–J4 utility logs and invoices.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md">
        {Field({ label: "Power Rate", k: "power_rate", unit: "/ kWh" })}
        {Field({ label: "Water Rate", k: "water_rate", unit: "/ m³" })}
      </div>
    </Card>
    <Button data-testid="pricing-save" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? "Saving..." : "Save Pricing"}</Button>
    </div>
  );
};

const MessagingPanel = () => {
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.getMessagingConfig().then(setM).catch(() => {}); }, []);
  if (!m) return <div className="text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>;
  const set = (k, v) => setM((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const updated = await api.adminUpdateMessaging({
        contact_name: m.contact_name,
        contact_phone: m.contact_phone,
        payment_reminder_template: m.payment_reminder_template,
        review_template_oto: m.review_template_oto,
        review_template_tk: m.review_template_tk,
        review_url_oto: m.review_url_oto,
        review_url_tk: m.review_url_tk,
      });
      setM(updated);
      toast.success("Messaging templates saved — now live across the app");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Card title="Payment Reminder" desc="Sent as an SMS via SMS Everyone. Placeholders: [Client Name], [Date 7 days from now], [Contact Name], [Contact Phone].">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl">
          <div className="space-y-1"><Label>Contact Name</Label><Input data-testid="msg-contact-name" value={m.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} /></div>
          <div className="space-y-1"><Label>Contact Phone</Label><Input data-testid="msg-contact-phone" value={m.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} /></div>
        </div>
        <div className="space-y-1">
          <Label>Reminder Message Template</Label>
          <Textarea data-testid="msg-payment-template" rows={4} value={m.payment_reminder_template || ""} onChange={(e) => set("payment_reminder_template", e.target.value)} />
        </div>
      </Card>

      <Card title="Google Review Request" desc="Triggered when a unit is marked Vacant or Needs Cleaning. Otorohanga & Razza use the Otorohanga link; Te Kuiti uses the Te Kuiti link. Placeholders: (NAME), [Review URL].">
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Otorohanga / Razza — Message</Label>
            <Textarea data-testid="msg-review-oto" rows={3} value={m.review_template_oto || ""} onChange={(e) => set("review_template_oto", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Otorohanga / Razza — Review URL</Label>
            <Input data-testid="msg-review-url-oto" value={m.review_url_oto || ""} onChange={(e) => set("review_url_oto", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Te Kuiti — Message</Label>
            <Textarea data-testid="msg-review-tk" rows={3} value={m.review_template_tk || ""} onChange={(e) => set("review_template_tk", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Te Kuiti — Review URL</Label>
            <Input data-testid="msg-review-url-tk" value={m.review_url_tk || ""} onChange={(e) => set("review_url_tk", e.target.value)} />
          </div>
        </div>
      </Card>

      <Button data-testid="msg-save" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? "Saving..." : "Save Messaging Settings"}</Button>
    </div>
  );
};

const AssetsPanel = () => {
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [siteFilter, setSiteFilter] = useState("ALL");
  const [form, setForm] = useState({ site: "", asset_name: "" });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({});
  const [skip, setSkip] = useState(0);
  const origin = window.location.origin;
  const assetUrl = (t) => `${origin}/maintenance/asset/${t}`;

  const load = () => api.listAssets(siteFilter).then((a) => { setAssets(a); setSelected(Object.fromEntries(a.map((x) => [x.id, true]))); }).catch(() => {});
  useEffect(() => { api.getSites().then((s) => { setSites(s); setForm((f) => ({ ...f, site: f.site || s[0]?.key || "" })); }).catch(() => {}); }, []);
  useEffect(() => { load(); }, [siteFilter]); // eslint-disable-line

  const add = async () => {
    if (!form.site || !form.asset_name.trim()) { toast.error("Site and asset name required"); return; }
    setBusy(true);
    try { await api.createAsset(form); toast.success("Asset registered"); setForm((f) => ({ ...f, asset_name: "" })); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (a) => {
    if (!window.confirm(`Delete asset "${a.asset_name}"? Its QR code will stop working.`)) return;
    try { await api.deleteAsset(a.id); toast.success("Asset deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedAssets = assets.filter((a) => selected[a.id]);

  const printAvery = () => {
    if (selectedAssets.length === 0) { toast.error("Tick at least one asset"); return; }
    const skipN = Math.max(0, Math.min(19, parseInt(skip, 10) || 0));
    const spacers = Array.from({ length: skipN }, () => `<div class="label spacer"></div>`).join("");
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
    const cells = selectedAssets.map((a) => {
      const c = document.querySelector(`#asset-qr-${a.id} canvas`);
      const url = c ? c.toDataURL("image/png") : "";
      return `<div class="label"><img src="${url}"/><div class="nm">${esc(a.asset_name)}</div><div class="st">${esc(a.site)}</div></div>`;
    }).join("");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(`<html><head><title>KCSS Asset Labels — Avery 45x45-S</title><style>
      @page { size: A4; margin: 36mm 15mm; }
      * { box-sizing: border-box; }
      body { margin:0; font-family: Arial, sans-serif; }
      .sheet { display:grid; grid-template-columns: repeat(4, 45mm); grid-auto-rows: 45mm; }
      .label { width:45mm; height:45mm; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:2mm; overflow:hidden; text-align:center; }
      .spacer { visibility:hidden; }
      .label img { width:29mm; height:29mm; }
      .nm { font-size:7pt; font-weight:bold; line-height:1.05; margin-top:1mm; }
      .st { font-size:6pt; color:#555; }
    </style></head><body><div class="sheet">${spacers}${cells}</div>
    <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`);
    w.document.close();
  };

  return (
    <div className="space-y-4">
      <Card title="Register an Asset" desc="Add equipment per site (gate motor, UPS, water pump). Each asset gets a unique QR code that logs jobs straight to its own history.">
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={form.site} onValueChange={(v) => setForm((f) => ({ ...f, site: v }))}>
            <SelectTrigger className="sm:w-[180px]" data-testid="asset-form-site"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="asset-form-name" value={form.asset_name} onChange={(e) => setForm((f) => ({ ...f, asset_name: e.target.value }))} placeholder="Asset name e.g. Main Gate Motor" className="flex-1" />
          <Button data-testid="asset-add-btn" onClick={add} disabled={busy} className="bg-orange-600 hover:bg-orange-700 gap-2"><Plus className="h-4 w-4" /> Add Asset</Button>
        </div>
      </Card>

      <Card title="Asset QR Codes & Avery 45×45-S Print" desc="Tick the assets to include, then print onto Avery 45x45-S sheets (20 square 45×45 mm labels per A4).">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-1">
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="sm:w-[180px]" data-testid="asset-site-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="ALL">All sites</SelectItem>{sites.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Label htmlFor="avery-skip" className="text-xs text-slate-500 whitespace-nowrap">Skip first N labels</Label>
            <Input id="avery-skip" data-testid="asset-skip-labels" type="number" min={0} max={19} value={skip}
              onChange={(e) => setSkip(Math.max(0, Math.min(19, parseInt(e.target.value, 10) || 0)))}
              className="w-[70px]" />
            <span className="text-[11px] text-slate-400 hidden sm:inline">(0–19, for part-used sheets)</span>
          </div>
          <Button data-testid="asset-print-sheet" onClick={printAvery} variant="outline" className="gap-2 border-slate-300 sm:ml-auto"><Printer className="h-4 w-4" /> Print Avery 45×45-S Sheet ({selectedAssets.length})</Button>
        </div>
        {assets.length === 0 ? (
          <p className="text-sm text-slate-400">No assets registered yet. Add one above.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {assets.map((a) => (
              <div key={a.id} id={`asset-qr-${a.id}`} data-testid={`asset-card-${a.id}`} className="rounded-xl border border-slate-200 p-3 flex flex-col items-center text-center gap-2">
                <label className="flex items-center gap-2 self-start text-xs text-slate-500 cursor-pointer">
                  <input type="checkbox" checked={!!selected[a.id]} onChange={() => toggle(a.id)} data-testid={`asset-check-${a.id}`} /> include
                </label>
                <div className="bg-white p-1.5 rounded-lg border border-slate-100"><QRCodeCanvas value={assetUrl(a.qr_code_token)} size={120} level="M" includeMargin /></div>
                <div className="text-sm font-semibold text-slate-800 leading-tight">{a.asset_name}</div>
                <span className="text-[11px] rounded-full px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200">{a.site}</span>
                <button onClick={() => del(a)} className="text-slate-300 hover:text-red-500" data-testid={`asset-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default function Admin() {
  const [params] = useSearchParams();
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-[1500px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Brand subtitle="Admin Console" />
          <ViewNav />
        </div>
      </header>
      <main className="max-w-[1500px] mx-auto px-5 py-6 space-y-5">
        <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-blue-600" /> Admin Console
        </h2>
        <Tabs defaultValue={params.get("tab") || "users"}>
          <TabsList data-testid="admin-tabs" className="w-full justify-start overflow-x-auto max-w-full">
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="sites" data-testid="admin-tab-sites">Sites &amp; Sheds</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="admin-tab-pricing">Pricing</TabsTrigger>
            <TabsTrigger value="messaging" data-testid="admin-tab-messaging">Messaging</TabsTrigger>
            <TabsTrigger value="assets" data-testid="admin-tab-assets">Assets &amp; QR</TabsTrigger>
            <TabsTrigger value="xero" data-testid="admin-tab-xero">Xero</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-4"><UsersPanel /></TabsContent>
          <TabsContent value="sites" className="mt-4"><SitesPanel /></TabsContent>
          <TabsContent value="pricing" className="mt-4"><PricingPanel /></TabsContent>
          <TabsContent value="messaging" className="mt-4"><MessagingPanel /></TabsContent>
          <TabsContent value="assets" className="mt-4"><AssetsPanel /></TabsContent>
          <TabsContent value="xero" className="mt-4"><XeroPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
